import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Autocomplete,
  TextField,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Paper,
  Tab,
  Tabs,
  useTheme,
  alpha
} from '@mui/material';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonIcon from '@mui/icons-material/Person';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import ElderlyIcon from '@mui/icons-material/Elderly';
import travelCosts from '../../../data/travel_costs.json';
import SiteWrapper from '../../home-pages/SiteWrapper'
import TravelChat from "./TravelChat"

const getLowestCost = (costsObj) => {
  if (!costsObj) return 0;
  const vals = [costsObj.first, costsObj.business, costsObj.prem_econ, costsObj.main_cabin];
  return Math.min(...vals);
};

const formatCost = (cost) => {
  if (cost === undefined || cost === null) return 'N/A';
  return cost === 0 ? "Free" : `$${cost.toFixed(2)}`;
};

const ClassCostRow = ({ label, cost, isLowest }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 1 }}>
    <Typography variant="body1" color="text.secondary">
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {isLowest && cost !== 0 && (
        <Chip label="Lowest" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
      )}
      <Typography variant="h6" fontWeight={isLowest ? "bold" : "normal"} color={isLowest ? 'primary.main' : 'text.primary'}>
        {formatCost(cost)}
      </Typography>
    </Box>
  </Box>
);

const BenefitCard = ({ title, icon, color, data }) => {
  const theme = useTheme();
  
  if (!data) {
    return (
      <Card sx={{ height: '100%', borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.05)' }}>
        <CardContent sx={{ textAlign: 'center', p: 4 }}>
          <Typography color="text.secondary">No data available for {title}</Typography>
        </CardContent>
      </Card>
    );
  }

  const lowestCost = getLowestCost(data);

  return (
    <Card 
      sx={{ 
        height: '100%', 
        borderRadius: 4, 
        boxShadow: `0 8px 32px ${alpha(color, 0.15)}`,
        borderTop: `6px solid ${color}`,
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
          <Box 
            sx={{ 
              backgroundColor: alpha(color, 0.1), 
              p: 1.5, 
              borderRadius: 2,
              display: 'flex',
              color: color
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Round-trip from DFW
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <ClassCostRow label="Main Cabin" cost={data.main_cabin} isLowest={data.main_cabin === lowestCost} />
          <ClassCostRow label="Premium Economy" cost={data.prem_econ} isLowest={data.prem_econ === lowestCost} />
          <ClassCostRow label="Business" cost={data.business} isLowest={data.business === lowestCost} />
          <ClassCostRow label="First Class" cost={data.first} isLowest={data.first === lowestCost} />
        </Box>
      </CardContent>
    </Card>
  );
};

const NonRevTravel = () => {
  const theme = useTheme();
  const [selectedDest, setSelectedDest] = useState(null)
  const [recentSearches, setRecentSearches] = useState([])
  const [activeTab, setActiveTab] = useState(0)

  // Load from local storage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('nonRevRecentSearches');
      if (stored) {
        setRecentSearches(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Failed to load recent searches', e);
    }
  }, []);

  const handleDestChange = (event, newValue) => {
    setSelectedDest(newValue);
    if (newValue) {
      // Add to recent searches if not already present
      setRecentSearches(prev => {
        const isExisting = prev.some(item => item.code === newValue.code);
        if (isExisting) return prev;
        
        const updated = [newValue, ...prev].slice(0, 5); // Keep top 5
        localStorage.setItem('nonRevRecentSearches', JSON.stringify(updated));
        return updated;
      });
    }
  };

  const handleRemoveRecent = (codeToRemove) => {
    setRecentSearches(prev => {
      const updated = prev.filter(item => item.code !== codeToRemove);
      localStorage.setItem('nonRevRecentSearches', JSON.stringify(updated));
      return updated;
    });
  };

  // Sorting destinations alphabetically for the dropdown
  const options = [...travelCosts].sort((a, b) => a.destination.localeCompare(b.destination));

  return (
    <SiteWrapper>
      <div className="main-page-wrapper">
        <Box sx={{ minHeight: "100vh", backgroundColor: theme.palette.grey[50], pt: 3, pb: 10 }}>
          <Container maxWidth="lg">

            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{ mb: 3 }}
            >
              <Tab label="Cost Lookup" sx={{ minHeight: 48, fontSize: "1rem" }} />
              <Tab label="AI Assistant" sx={{ minHeight: 48, fontSize: "1rem" }} />
            </Tabs>

            {activeTab === 0 && (
              <Box>
                {/* Header & Search Bar Row */}
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, md: 3 },
                    borderRadius: 4,
                    boxShadow: "0 12px 40px rgba(0,0,0,0.04)",
                    mb: 4,
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "flex-start", md: "center" },
                    gap: 3,
                    border: "1px solid",
                    borderColor: theme.palette.divider,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                    <Box
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        p: 1.5,
                        borderRadius: 2,
                        display: "flex",
                        color: theme.palette.primary.main,
                      }}
                    >
                      <FlightTakeoffIcon sx={{ fontSize: 32 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="bold" sx={{ letterSpacing: "-0.5px" }}>
                        Non-Rev Travel
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        DFW Standby Costs
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        Costs as of Jan 2026 — may not reflect current rates
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ flexGrow: 1, width: "100%" }}>
                    <Autocomplete
                      options={options}
                      getOptionLabel={(option) => `${option.destination} (${option.code})`}
                      value={selectedDest}
                      onChange={handleDestChange}
                      isOptionEqualToValue={(option, value) => option.code === value.code}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Where do you want to go?"
                          variant="outlined"
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            sx: { borderRadius: 3, fontSize: "1.2rem", py: 1.5 },
                          }}
                          InputLabelProps={{
                            sx: { fontSize: "1.2rem" },
                          }}
                        />
                      )}
                      renderOption={(props, option) => {
                        const { key, ...optionProps } = props
                        return (
                          <li key={key} {...optionProps}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                              <Typography>{option.destination}</Typography>
                              <Typography
                                color="text.secondary"
                                variant="body2"
                                sx={{
                                  ml: 2,
                                  fontWeight: "bold",
                                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                }}
                              >
                                {option.code}
                              </Typography>
                            </Box>
                          </li>
                        )
                      }}
                      sx={{ width: "100%" }}
                    />

                    {!selectedDest && recentSearches.length === 0 && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5, color: "text.secondary", pl: 1 }}>
                        <InfoOutlinedIcon fontSize="small" />
                        <Typography variant="body2">Search by city or airport code to see benefit costs.</Typography>
                      </Box>
                    )}

                    {recentSearches.length > 0 && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2, flexWrap: "wrap" }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                          Recent:
                        </Typography>
                        {recentSearches.map((recent) => (
                          <Chip
                            key={recent.code}
                            label={recent.destination}
                            size="small"
                            onClick={() => handleDestChange(null, recent)}
                            onDelete={() => handleRemoveRecent(recent.code)}
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.15) },
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Paper>

                {/* Results */}
                {selectedDest && (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 2, mb: 4, ml: 1 }}>
                      <Typography variant="h4" fontWeight="bold">
                        {selectedDest.destination}
                      </Typography>
                      <Chip
                        label={selectedDest.code}
                        color="primary"
                        variant="soft"
                        sx={{ fontWeight: "bold", fontSize: "1.1rem" }}
                      />
                      <Typography variant="body1" color="text.secondary" sx={{ ml: "auto" }}>
                        Distance: {selectedDest.miles.toLocaleString()} miles
                      </Typography>
                    </Box>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <BenefitCard
                          title="Family (D2)"
                          icon={<FamilyRestroomIcon fontSize="large" />}
                          color={theme.palette.primary.main}
                          data={selectedDest.costs?.D2}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <BenefitCard
                          title="Mom (D2P)"
                          icon={<PersonIcon fontSize="large" />}
                          color={theme.palette.secondary.main}
                          data={selectedDest.costs?.D2P}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <BenefitCard
                          title="Father-in-Law (D2P)"
                          icon={<ElderlyIcon fontSize="large" />}
                          color={theme.palette.success.main}
                          data={selectedDest.costs?.D2PI || selectedDest.costs?.D3}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box>
            )}

            {activeTab === 1 && (
              <Box
                sx={{
                  height: { xs: "calc(100dvh - 220px)", sm: "calc(100dvh - 200px)", md: "70vh" },
                  minHeight: 420,
                  borderRadius: 3,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
                }}
              >
                <TravelChat />
              </Box>
            )}

          </Container>
        </Box>
      </div>
    </SiteWrapper>
  )
};

export default NonRevTravel;
